import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { FileText, TrendingUp, Target, MessageSquare, Users, Calendar, Plus, Trash2, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { icon: FileText, label: "Mental Training Plan", path: "/plans" },
  { icon: TrendingUp, label: "Mental Performance", path: "/performance" },
  { icon: Target, label: "Goals", path: "/goals" },
  { icon: MessageSquare, label: "AI Coach", path: "/ai-coach" },
  { icon: Users, label: "Teams", path: "/teams" }
];

export default function Routines({ user, onLogout }) {
  const [routines, setRoutines] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [selectedType, setSelectedType] = useState("all");
  const [formData, setFormData] = useState({
    title: "",
    routine_type: "game-day",
    description: "",
    steps: [{ order: 1, activity: "", duration_minutes: 5, notes: "" }]
  });

  useEffect(() => {
    fetchRoutines();
  }, [selectedType]);

  const fetchRoutines = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/routines`;
      if (selectedType !== "all") url += `?routine_type=${selectedType}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoutines(response.data);
    } catch (error) {
      toast.error("Failed to load routines");
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || formData.steps.length === 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/routines`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Routine created!");
      setShowCreate(false);
      resetForm();
      fetchRoutines();
    } catch (error) {
      toast.error("Failed to create routine");
    }
  };

  const handleDeleteRoutine = async (routineId) => {
    if (!window.confirm("Are you sure you want to delete this routine?")) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/routines/${routineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Routine deleted");
      fetchRoutines();
    } catch (error) {
      toast.error("Failed to delete routine");
    }
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, { order: formData.steps.length + 1, activity: "", duration_minutes: 5, notes: "" }]
    });
  };

  const removeStep = (index) => {
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      steps: newSteps.map((step, i) => ({ ...step, order: i + 1 }))
    });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = value;
    setFormData({ ...formData, steps: newSteps });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      routine_type: "game-day",
      description: "",
      steps: [{ order: 1, activity: "", duration_minutes: 5, notes: "" }]
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      "game-day": "from-red-400 to-orange-400",
      "practice": "from-blue-400 to-cyan-400",
      "mental-performance": "from-purple-400 to-pink-400"
    };
    return colors[type] || "from-gray-400 to-gray-500";
  };

  const getTypeIcon = (type) => {
    const icons = {
      "game-day": "🏆",
      "practice": "💪",
      "mental-performance": "🧠"
    };
    return icons[type] || "📋";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Routines & Rituals
            </h1>
            <p className="text-lg text-gray-600">
              Build consistent pre-game, practice, and mental performance routines
            </p>
          </div>
          <Button
            data-testid="create-routine-btn"
            onClick={() => setShowCreate(true)}
            className="bg-sky-600 hover:bg-sky-700"
          >
            Create Routine
          </Button>
        </div>

        {/* Type Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { value: "all", label: "All Routines" },
              { value: "game-day", label: "Game Day" },
              { value: "practice", label: "Practice" },
              { value: "mental-performance", label: "Mental Performance" }
            ].map((type) => (
              <button
                key={type.value}
                data-testid={`filter-${type.value}`}
                onClick={() => setSelectedType(type.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedType === type.value
                    ? 'bg-sky-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Routines Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {routines.map((routine) => (
            <Card
              key={routine.id}
              data-testid={`routine-card-${routine.id}`}
              className="border-0 shadow-lg bg-white/90 backdrop-blur-sm hover:shadow-xl transition-all duration-300"
            >
              <div className={`h-20 bg-gradient-to-r ${getTypeColor(routine.routine_type)} rounded-t-lg flex items-center justify-between px-6`}>
                <div className="flex items-center space-x-3">
                  <span className="text-4xl">{getTypeIcon(routine.routine_type)}</span>
                  <span className="text-white font-semibold text-lg">
                    {routine.routine_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                <Button
                  data-testid={`delete-routine-${routine.id}`}
                  onClick={() => handleDeleteRoutine(routine.id)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <CardHeader>
                <CardTitle className="text-xl">{routine.title}</CardTitle>
                {routine.description && (
                  <CardDescription className="text-base">{routine.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-gray-700">Steps:</h4>
                  {routine.steps.slice(0, 3).map((step, index) => (
                    <div key={index} className="flex items-start space-x-3 text-sm">
                      <span className="flex-shrink-0 w-6 h-6 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center text-xs font-semibold">
                        {step.order}
                      </span>
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium">{step.activity}</p>
                        {step.notes && <p className="text-gray-600 text-xs mt-1">{step.notes}</p>}
                        <p className="text-gray-500 text-xs mt-1">⏱️ {step.duration_minutes} min</p>
                      </div>
                    </div>
                  ))}
                  {routine.steps.length > 3 && (
                    <p className="text-xs text-gray-500 italic">
                      +{routine.steps.length - 3} more steps
                    </p>
                  )}
                  <Button
                    data-testid={`view-routine-${routine.id}`}
                    onClick={() => setSelectedRoutine(routine)}
                    variant="outline"
                    size="sm"
                    className="w-full mt-4"
                  >
                    View Full Routine
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {routines.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              No routines yet. Create your first routine to build consistency!
            </p>
            <Button
              data-testid="create-first-routine-btn"
              onClick={() => setShowCreate(true)}
              className="bg-sky-600 hover:bg-sky-700"
            >
              Create Your First Routine
            </Button>
          </div>
        )}
      </div>

      {/* Create Routine Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-routine-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Create New Routine</DialogTitle>
            <DialogDescription>Build a step-by-step routine to follow consistently</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label htmlFor="title">Routine Title *</Label>
              <Input
                data-testid="routine-title-input"
                id="title"
                placeholder="e.g., Championship Game Day Routine"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="routine_type">Routine Type *</Label>
              <select
                data-testid="routine-type-select"
                id="routine_type"
                value={formData.routine_type}
                onChange={(e) => setFormData({ ...formData, routine_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-white"
              >
                <option value="game-day">Game Day</option>
                <option value="practice">Practice</option>
                <option value="mental-performance">Mental Performance</option>
              </select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                data-testid="routine-description-textarea"
                id="description"
                placeholder="Brief description of this routine"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <Label>Routine Steps *</Label>
                <Button
                  data-testid="add-step-btn"
                  onClick={addStep}
                  size="sm"
                  variant="outline"
                  className="text-sky-600"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add Step
                </Button>
              </div>
              <div className="space-y-4">
                {formData.steps.map((step, index) => (
                  <Card key={index} className="p-4 bg-gray-50">
                    <div className="flex items-start space-x-3">
                      <span className="flex-shrink-0 w-8 h-8 bg-sky-600 text-white rounded-full flex items-center justify-center font-semibold">
                        {step.order}
                      </span>
                      <div className="flex-1 space-y-3">
                        <Input
                          data-testid={`step-activity-${index}`}
                          placeholder="Activity (e.g., Visualization practice)"
                          value={step.activity}
                          onChange={(e) => updateStep(index, 'activity', e.target.value)}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Duration (minutes)</Label>
                            <Input
                              data-testid={`step-duration-${index}`}
                              type="number"
                              min="1"
                              value={step.duration_minutes}
                              onChange={(e) => updateStep(index, 'duration_minutes', parseInt(e.target.value))}
                            />
                          </div>
                        </div>
                        <Textarea
                          data-testid={`step-notes-${index}`}
                          placeholder="Notes (optional)"
                          value={step.notes}
                          onChange={(e) => updateStep(index, 'notes', e.target.value)}
                          rows={2}
                        />
                      </div>
                      {formData.steps.length > 1 && (
                        <Button
                          data-testid={`remove-step-${index}`}
                          onClick={() => removeStep(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <Button
              data-testid="submit-routine-btn"
              onClick={handleSubmit}
              className="w-full bg-sky-600 hover:bg-sky-700 text-lg py-6"
            >
              Create Routine
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Routine Dialog */}
      <Dialog open={!!selectedRoutine} onOpenChange={() => setSelectedRoutine(null)}>
        <DialogContent data-testid="view-routine-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className={`-mx-6 -mt-6 h-24 bg-gradient-to-r ${getTypeColor(selectedRoutine?.routine_type)} rounded-t-lg flex items-center px-6 mb-4`}>
              <div className="flex items-center space-x-3">
                <span className="text-5xl">{getTypeIcon(selectedRoutine?.routine_type)}</span>
                <div>
                  <DialogTitle className="text-2xl text-white">{selectedRoutine?.title}</DialogTitle>
                  <p className="text-white/90 text-sm">
                    {selectedRoutine?.routine_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                </div>
              </div>
            </div>
            {selectedRoutine?.description && (
              <DialogDescription className="text-base">{selectedRoutine.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4">
              {selectedRoutine?.steps.map((step, index) => (
                <Card key={index} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-4">
                    <span className="flex-shrink-0 w-10 h-10 bg-sky-600 text-white rounded-full flex items-center justify-center text-lg font-bold">
                      {step.order}
                    </span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-lg text-gray-900 mb-2">{step.activity}</h4>
                      {step.notes && (
                        <p className="text-gray-600 text-sm mb-2">{step.notes}</p>
                      )}
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>{step.duration_minutes} minutes</span>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="mt-6 p-4 bg-sky-50 rounded-lg border border-sky-200">
              <p className="text-sm text-sky-900">
                <strong>Total Duration:</strong> {selectedRoutine?.steps.reduce((acc, step) => acc + step.duration_minutes, 0)} minutes
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}